import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IMPORT_MESSAGE_MAX_LENGTH,
  type ImportMessage,
  type ImportQuestionOption,
  type ImportSession,
  type SendImportMessageBody,
} from '@ni/shared';
import { useSendImportMessage } from '../../api/hooks';
import { errorLine, hasCode } from '../../api/errors';
import {
  IMPORT_CHAT_CHANGE_ANSWER,
  IMPORT_CHAT_EMPTY,
  IMPORT_CHAT_HINT,
  IMPORT_CHAT_KEEP_ANSWER,
  IMPORT_CHAT_NOT_ASKED,
  IMPORT_CHAT_PLACEHOLDER,
  IMPORT_CHAT_RETRY,
  IMPORT_CHAT_SENDING,
  IMPORT_CHAT_SENDING_MESSAGE,
  IMPORT_CHAT_WORKING_ANSWERS,
  IMPORT_NOTES_LABEL,
  IMPORT_PLAN_MESSAGE_LINK,
  characterCount,
  defaultOptionSuffix,
  importAlreadyAnsweredLine,
  importSendLabel,
  importWaitingLine,
} from '../../lib/copy';
import { openingTurnState } from '../../routes/import/open-turn';
import {
  answeredLines,
  answeredQuestionIds,
  buildMessageBody,
  buildPicks,
  messageAwaitingReply,
  retryBody,
  standingQuestions,
  type PendingPick,
} from '../../routes/import/standing-questions';
import { formatClock, formatElapsed } from '../../lib/format';
import { CollapsedBin } from '../ui/collapsed-bin';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { RetryWithThisFile } from './retry-file';
import { EmptyLine } from '../ui/empty-line';

/**
 * **The session's conversation** (import note §1.2) — the office says what the
 * file is, the planner asks what the profile and the description did not settle,
 * and a plan comes back.
 *
 * Nothing is running between turns: every message is one stateless call over the
 * transcript, and the transcript is on the session read. So this is a document
 * that is rebuilt on every load, oldest first, and never a stream.
 *
 * **A question is a button per option, and the buttons are a batch** (§6.3).
 * The planner offers a handful of sensible answers where there are any, and
 * pressing one *records* that answer against the id of the question it settles —
 * without the pairing the next turn shows the model a transcript of loose
 * sentences and it re-asks what it has already been told, which in a six-turn
 * session is the whole of the cost. Pressing one used to send a whole turn; now
 * the picks are held here until Send and go up together with whatever was typed.
 *
 * Split the way the rest of the app splits: the view half takes what is on the
 * page, the outer half owns the two writes.
 */

/**
 * **The opening turn, handed down rather than owned here** (§1.2).
 *
 * The session page fires it on arrival and this renders where it has got to, so
 * there is one mutation between them: one pending, one failure, and a Retry that
 * presses the same thing the page already tried.
 */
export interface OpeningTurn {
  /** Ask the planner again — what the Retry button presses. */
  fire: () => void;
  pending: boolean;
  failed: boolean;
  error: unknown;
}

const NO_PICKS: ReadonlyMap<string, PendingPick> = new Map();
const NOTHING_REOPENED: ReadonlySet<string> = new Set();

/**
 * **A clock that counts up, ticking once a second** (§6.3).
 *
 * A planner turn is seconds to a couple of minutes and the page no longer greys
 * while it runs, so the one thing the office has to tell a slow turn from a lost
 * one is a number that moves. `null` is "nothing is running", and the interval
 * is not armed at all then: a chat sitting idle should not be re-rendering once
 * a second for the rest of the afternoon.
 */
export function useElapsed(since: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (since === null) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, [since]);

  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1_000));
}

export function ImportChat({
  importId,
  session,
  opening,
}: {
  importId: string;
  session: ImportSession;
  opening: OpeningTurn;
}) {
  const send = useSendImportMessage(importId);
  const [typed, setTyped] = useState('');
  /**
   * **The batch** (§6.3) — the options pressed and not yet sent, by the id of
   * the question each one settles. It lives in the tab and nowhere else: a
   * refresh loses it, which is honest, because the server had no record of it.
   */
  const [picks, setPicks] = useState<ReadonlyMap<string, PendingPick>>(NO_PICKS);
  /**
   * The settled questions whose options are open again — what **Change answer**
   * presses. Held beside the picks rather than inside them because reopening a
   * question is not yet answering it, and Keep has to be able to close one that
   * nobody has re-answered.
   */
  const [reopened, setReopened] = useState<ReadonlySet<string>>(NOTHING_REOPENED);
  /**
   * **The exact body of the turn that failed** (D13).
   *
   * The retry used to re-send `submit(typed)` — the composer's current text —
   * which is wrong twice over. A turn that failed was almost always an **answer
   * to a question**: the batch carries the option's words *with the id of the
   * question it settles*, and the composer is usually empty throughout. So the
   * retry either sent a loose sentence with no question id attached, and the
   * next turn re-asked what it had already been told, or — the common case, an
   * empty composer — it was refused by the guard on the way out and did nothing
   * at all. A button that cannot do anything is worse than no button.
   */
  const [failed, setFailed] = useState<SendImportMessageBody | null>(null);
  /**
   * What is in flight and since when — the two things the line under the
   * composer says. `answers` rather than a re-read of the body because the line
   * is about what the office pressed, and the body is gone by the time it fails.
   */
  const [running, setRunning] = useState<{ since: number; answers: boolean } | null>(null);

  const working = send.isPending && running !== null;
  const elapsed = useElapsed(working ? running.since : null);

  /* The planner is only reachable while the session is still a session: a
     committed, canceled or unwound one answers `IMPORT_NOT_EDITABLE`, and a
     composer that could only be refused is worse than none. */
  const editable = session.state === 'in_progress';

  const post = (body: SendImportMessageBody) => {
    if (send.isPending) return;
    setRunning({ since: Date.now(), answers: (body.answers?.length ?? 0) > 0 });
    send.mutate(body, {
      onSuccess: () => {
        setTyped('');
        setPicks(NO_PICKS);
        setReopened(NOTHING_REOPENED);
        setFailed(null);
        setRunning(null);
      },
      onError: (error) => {
        setFailed(body);
        setRunning(null);
        /* **The server already held those answers** — `409
           IMPORT_QUESTION_ANSWERED`, which is what a double press finds. Drop
           exactly the ids it named and keep everything else the office picked;
           the mutation's own `onSettled` refetches the session, so the questions
           it refused redraw as answered from the record. */
        const already = answeredQuestionIds(error);
        if (already.length === 0) return;
        setPicks((current) => {
          const next = new Map(current);
          for (const id of already) next.delete(id);
          return next;
        });
        setReopened((current) => {
          const next = new Set(current);
          for (const id of already) next.delete(id);
          return next;
        });
      },
    });
  };

  /* One rule for what Send sends and whether there is anything to send — the
     button is disabled by the same answer that builds the body.

     **The record, as this render sees it**, is the third argument: which picks
     are deliberate repeats is a fact about the transcript and it is read here,
     at Send, rather than frozen when the option was pressed. A turn that failed
     at the model wrote the office's row on its way, so a question that was open
     at the press can be settled by the time Send comes round again. */
  const standing = standingQuestions(session.messages);
  const body = buildMessageBody(typed, picks, standing);

  /**
   * A pick is recorded, not sent. Pressing the option that is already picked
   * takes it back off the batch, which is the only way out of a pick that does
   * not involve sending one.
   */
  const choose = (questionId: string, text: string) => {
    setPicks((current) => buildPicks(current, questionId, text));
  };

  const keep = (questionId: string) => {
    setPicks((current) => {
      if (!current.has(questionId)) return current;
      const next = new Map(current);
      next.delete(questionId);
      return next;
    });
    setReopened((current) => {
      const next = new Set(current);
      next.delete(questionId);
      return next;
    });
  };

  /* The opening turn is the only one nobody pressed a button for, so it is the
     only one whose absence has to be explained on the page. */
  const opener = openingTurnState(session, opening);

  return (
    <div className="import-chat">
      {/* **Why the transcript is empty, said out loud** (C6) — and said once.
          The turn runs on arrival and takes up to two minutes, so for most of
          the office's first look at a session there is nothing here at all;
          what says so is the tinted line under the head, which is where every
          other pulse on this page is. A second one in this column, four inches
          below the first and in the same green, was two answers to one
          question. So while it is working this column shows nothing rather than
          "Nothing has been said yet.", which would be true and useless. */}
      {opener === 'working' ? null : (
        <ImportChatTranscript
          importId={importId}
          messages={session.messages}
          /* **The marker for a turn this tab is not running** (§6.3). A refresh
             mid-turn loses the mutation and leaves a transcript ending in the
             office's own message; while the mutation *is* here, the line under
             the composer says the same thing and one of the two is enough.

             And a turn that came back refused is not a turn anybody is waiting
             on: the failure is already said under the composer, and "Waiting for
             the planner's reply…" under the message it refused is the page
             saying two different things about one turn. */
          waiting={editable && !send.isPending && !send.isError}
        />
      )}

      {/* When the turn does not come back — losing a 25 MB upload because a
          model was briefly unreachable would be the worse failure by far, so the
          session stands either way — this says so and offers it again. */}
      {opener === 'retry' ? (
        <div className="layer-actions">
          <div className="src">{IMPORT_CHAT_NOT_ASKED}</div>
          <WorkingButton
            label={IMPORT_CHAT_RETRY}
            workingLabel={IMPORT_CHAT_SENDING}
            working={opening.pending}
            onClick={opening.fire}
          />
          {opening.failed ? <ErrorLine error={opening.error} /> : null}
        </div>
      ) : null}

      {editable ? (
        <>
          <ImportQuestions
            messages={session.messages}
            busy={send.isPending}
            picks={picks}
            reopened={reopened}
            onPick={choose}
            onReopen={(questionId) => setReopened((current) => new Set(current).add(questionId))}
            onKeep={keep}
          />

          <div className="import-composer">
            {/* **The way out of the buttons**, in one line and in the "or" that
                says the buttons above are the ordinary answer. It replaces the
                textarea's own placeholder, which said the same words twice in
                two type sizes an inch apart. */}
            <div className="src composer-hint">{IMPORT_CHAT_HINT}</div>
            <textarea
              className="control"
              aria-label={IMPORT_CHAT_PLACEHOLDER}
              maxLength={IMPORT_MESSAGE_MAX_LENGTH}
              rows={3}
              value={typed}
              disabled={send.isPending}
              onChange={(event) => setTyped(event.target.value)}
            />
            <div className="src">{characterCount(typed.length, IMPORT_MESSAGE_MAX_LENGTH)}</div>
            <div className="layer-actions">
              <WorkingButton
                label={importSendLabel(picks.size)}
                workingLabel={IMPORT_CHAT_SENDING}
                working={send.isPending}
                disabled={body === null}
                onClick={() => {
                  if (body !== null) post(body);
                }}
              />
              {/* Nobody answered, and a moment later they might — the one import
                  refusal whose recourse is the same button again. It re-sends
                  the body that failed, answers and all, which is what makes it a
                  retry rather than a second, different turn — under `retry`,
                  because the row carrying those answers was written before the
                  model was called and the same body unchanged would now be
                  refused as a repeat. `retry` is the narrow word for that: say
                  the record again, and be refused where somebody else has since
                  said something different. See {@link retryBody}. */}
              {failed && hasCode(send.error, 'IMPORT_PLANNER_UNAVAILABLE') ? (
                <WorkingButton
                  label={IMPORT_CHAT_RETRY}
                  workingLabel={IMPORT_CHAT_SENDING}
                  variant="act"
                  working={send.isPending}
                  onClick={() => post(retryBody(failed))}
                />
              ) : null}
            </div>
            {/* **What is happening, while nothing greys** (§6.3) — one line, and
                a clock, because a turn that rebuilds every row inside its
                transaction is a slow turn and silence is how a slow one starts
                to read as a lost one.

                `role="status"` and a polite live region because the grey-out was
                the old cue and it is gone: a screen reader had the whole page go
                unavailable and now has nothing at all unless this line announces
                itself. Polite rather than assertive — a turn starting is not an
                interruption — and the clock's own ticks do not re-announce,
                since a status region reads its changed text and this one changes
                by a second at a time on a line that is already spoken. */}
            {working ? (
              <div className="src import-working" role="status" aria-live="polite">
                {running.answers ? IMPORT_CHAT_WORKING_ANSWERS : IMPORT_CHAT_SENDING_MESSAGE}{' '}
                {formatElapsed(elapsed)}
              </div>
            ) : null}
            {send.isError ? (
              <div className="src">
                {/* A refusal that is information rather than a failure: the
                    planner had those answers already, and the rest of the batch
                    is still here — when there is a rest. The named picks are
                    already out of `picks` by the time this draws, so the second
                    sentence is earned by what is left rather than promised. */}
                {hasCode(send.error, 'IMPORT_QUESTION_ANSWERED')
                  ? importAlreadyAnsweredLine(picks.size > 0)
                  : errorLine(send.error)}
              </div>
            ) : null}
          </div>
        </>
      ) : (
        /* **Where the composer was** (§4.2, 2026-09-14). A finished session's
           conversation is a document, and until now the column simply ended:
           the office read a transcript that stops mid-thought with no way to say
           the next thing. The next thing is a new session on the same file,
           carrying the plan this one settled and the answers it was given, so
           that is what stands here instead of a composer that could only be
           refused.

           It draws nothing on a session with no upload behind it — the batch
           `dossier:import` opens for itself is the one — which is its own rule
           rather than a second copy of one here. */
        <RetryWithThisFile session={session} />
      )}
    </div>
  );
}

/**
 * **The standing questions** — the ones on the newest agent turn, each drawn as
 * the transcript leaves it (§6.3).
 *
 * An older turn's questions were either answered or superseded by the turn that
 * followed, and offering them again would be asking the office to settle
 * something the planner has stopped asking about. **An answered one is its text
 * and its answer, and no buttons**: the answer is read off the transcript by
 * {@link standingQuestions}, so a refresh shows what the last one showed instead
 * of putting the options back under a question that is already settled.
 *
 * Re-answering is a deliberate act, which is what **Change answer** is: it
 * reopens that one question, the new pick joins the next batch, and the body
 * names it under `changes` so the server knows the repeat is meant.
 */
export function ImportQuestions({
  messages,
  busy,
  picks = NO_PICKS,
  reopened = NOTHING_REOPENED,
  onPick,
  onReopen,
  onKeep,
}: {
  messages: readonly ImportMessage[];
  busy: boolean;
  picks?: ReadonlyMap<string, PendingPick>;
  reopened?: ReadonlySet<string>;
  onPick: (questionId: string, text: string) => void;
  onReopen: (questionId: string) => void;
  onKeep: (questionId: string) => void;
}) {
  const standing = standingQuestions(messages);
  if (standing.length === 0) return null;

  return (
    <div className="import-questions">
      {standing.map(({ question, answered }) => {
        const pick = picks.get(question.id);
        /* Settled, and left settled: an answer on the record that nobody has
           reopened and nobody has re-picked. */
        const settled = answered !== null && pick === undefined && !reopened.has(question.id);

        return (
          <div className="import-question" key={question.id}>
            {/* The question in the body's own type. It was set in the chip's
                10.5px letterspaced capitals, which is the grammar this app uses
                for *labels over things* — and read as one, so a sentence somebody
                has to answer looked like a heading over the buttons. */}
            <div className="qtext">{question.text}</div>

            {settled ? (
              <>
                <div className="qanswer">{answered.text}</div>
                <button
                  type="button"
                  className="linkish"
                  disabled={busy}
                  onClick={() => onReopen(question.id)}
                >
                  {IMPORT_CHAT_CHANGE_ANSWER}
                </button>
              </>
            ) : (
              <>
                {question.options ? (
                  <div className="qoptions">
                    {answersFirst(question.options).map((option) => {
                      const picked = pick?.text === option.label;
                      return (
                        <button
                          key={option.label}
                          type="button"
                          className={picked ? 'pill picked' : 'pill'}
                          aria-pressed={picked}
                          disabled={busy}
                          /* The whole label, for the one the CSS had to cut. The
                             model is told five words; a title costs nothing and
                             is the difference between a clipped button and a
                             lost answer. */
                          title={option.label}
                          onClick={() => onPick(question.id, option.label)}
                        >
                          {option.label}
                          {option.default ? (
                            <span className="as-planned">
                              {' '}
                              {defaultOptionSuffix(question.text)}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {/* The way back out of a question that was already settled, for
                    an office that pressed Change answer and then thought better
                    of it. Nothing to say on a question that was never answered. */}
                {answered !== null ? (
                  <button
                    type="button"
                    className="linkish"
                    disabled={busy}
                    onClick={() => onKeep(question.id)}
                  >
                    {IMPORT_CHAT_KEEP_ANSWER}
                  </button>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * **The answer the plan already holds, first.**
 *
 * The planner is told to list it first and does; this is the guarantee rather
 * than the hope, because the order is what makes the row of buttons readable —
 * the marked one leads, and everything after it is a change. A stable sort, so
 * two unmarked options keep the order the planner chose to put them in.
 */
const answersFirst = (options: readonly ImportQuestionOption[]): ImportQuestionOption[] => [
  ...options.filter((option) => option.default),
  ...options.filter((option) => !option.default),
];

/**
 * **The turn nobody in this tab is running** (§6.3) — the line on the last
 * message when the office's own turn is out and the mutation that sent it is
 * gone, which is what a refresh mid-turn leaves behind.
 *
 * Past three minutes it stops counting and says what to do instead; the model
 * call is bounded well under that.
 *
 * **And nothing at all for the first few seconds** (`IMPORT_WAITING_AFTER_SECONDS`).
 * A send that has just succeeded leaves precisely this shape for as long as the
 * refetch takes — the mutation settled, the office's own message last on the
 * page, the planner's reply still in flight — so an unfloored marker flashed on
 * every successful turn. `importWaitingLine` answers `null` under the floor and
 * this draws nothing, rather than a line that has to be explained away.
 */
function WaitingForReply({ at }: { at: string }) {
  const since = Date.parse(at);
  const elapsed = useElapsed(Number.isNaN(since) ? null : since);
  const line = importWaitingLine(elapsed);
  /* A live region for the same reason the working line is one: the page no
     longer greys while a turn is out, so this marker is the only thing that says
     a reply is owed, and a reader that never sees it is a reader told nothing. */
  return line === null ? null : (
    <div className="src import-waiting" role="status" aria-live="polite">
      {line}
    </div>
  );
}

/**
 * **The rows the build before the batch wrote** (§6.3): pressing one pill sent a
 * turn whose `text` *was* the option's own label, with the same words again
 * under its single answer. Read back by a transcript that now prints the answers
 * as question-beside-answer, that row says the office's words twice, an inch
 * apart, once as a sentence they typed and once as the answer they pressed —
 * and they typed nothing.
 *
 * Narrow on purpose: one answer, and the text is exactly it. A turn that
 * answered one question *and* typed something else is two things and reads as
 * two, and a batch of several answers never had this shape to begin with.
 */
const echoesItsOnlyAnswer = (message: ImportMessage): boolean =>
  message.role === 'user' &&
  message.answers?.length === 1 &&
  message.text === message.answers[0]?.text;

/** The transcript, oldest first — what was said, and what each turn produced. */
export function ImportChatTranscript({
  importId,
  messages,
  waiting = false,
}: {
  importId: string;
  messages: readonly ImportMessage[];
  /**
   * Whether a turn is owed on the session at all. The *which message* half is
   * this component's own rule — the last one, when it is the office's — because
   * that is the shape a refresh mid-turn leaves the transcript in.
   */
  waiting?: boolean;
}) {
  if (messages.length === 0) {
    return <EmptyLine>{IMPORT_CHAT_EMPTY}</EmptyLine>;
  }

  const awaiting = waiting ? messageAwaitingReply(messages) : null;

  return (
    <div className="import-transcript">
      {messages.map((message) => (
        <div
          className={message.role === 'agent' ? 'import-turn agent' : 'import-turn user'}
          key={`${message.seq}-${message.at}`}
        >
          <div className="tag-faint">
            {message.role === 'agent' ? 'Assistant' : 'You'} · {formatClock(message.at)}
          </div>
          {message.text.length > 0 && !echoesItsOnlyAnswer(message) ? (
            <div className="turn-text">{message.text}</div>
          ) : null}

          {message.notes && message.notes.length > 0 ? (
            <CollapsedBin
              label={`${IMPORT_NOTES_LABEL} (${message.notes.length})`}
              openLabel="Hide the columns worth a look"
            >
              {message.notes.map((note, index) => (
                <div className="bin-row" key={`${message.seq}-note-${index}`}>
                  <div className="bname">{note.column}</div>
                  <div className="bdetail">{note.why}</div>
                </div>
              ))}
            </CollapsedBin>
          ) : null}

          {message.plan_version !== null ? (
            <div className="src">
              <Link className="link" to={`/imports/${importId}`}>
                {IMPORT_PLAN_MESSAGE_LINK}
              </Link>{' '}
              · version {message.plan_version}
            </div>
          ) : null}

          {/* **What this turn answered, in sentences** (§6.3). It was a count —
              "1 question answered" — under the one kind of turn the office most
              needs to be able to read back: which question, and what they said
              to it. The question's words come off the asking this answer
              belongs to, which is the nearest one before it. */}
          {message.answers && message.answers.length > 0 ? (
            <div className="turn-answers">
              {answeredLines(messages, message).map((line, index) => (
                <div className="answered-line" key={`${message.seq}-answer-${index}`}>
                  <div className="aq">{line.question}</div>
                  <div className="aa">{line.answer}</div>
                </div>
              ))}
            </div>
          ) : null}

          {awaiting && awaiting.seq === message.seq && awaiting.at === message.at ? (
            <WaitingForReply at={message.at} />
          ) : null}
        </div>
      ))}
    </div>
  );
}
